import { ButtonFrame, Card, CardSearch, InputDropdown, InputField, PageBodyHeader, Table, UploadField } from "../../component";
import { useEffect, useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../redux";
import { checkStoreByStoreIdUserId, createOrderTracker, fetchOrderRequest, fetchOrderTracker, fetchOrderTrackerSummary } from "../../action/generalFunc";
import ModalParent from "../../component/modal/ModalParent";
import { generalObj } from "../../constant/object_types";
import { toast } from "react-toastify";

const formatMonth = (month:string) => {
    if (!month) return "-";
    const [year, monthNumber] = month.split("-");
    const monthDate = new Date(Number(year), Number(monthNumber) - 1);
    return Number.isNaN(monthDate.getTime()) ? month : `${monthDate.toLocaleString("en-US", {month:"short"})} ${year}`;
};

const statusBadgeColor = (status:string) => {
    switch (status.toLowerCase()) {
        case "fulfilled":
            return { backgroundColor: "#E4F7EA", color: "#00983A" };
        case "cancelled":
            return { backgroundColor: "#FCE4E4", color: "#EB212F" };
        case "pending":
            return { backgroundColor: "#FFF3D6", color: "#B5820B" };
        default:
            return { backgroundColor: "#E9EAEB", color: "#676967" };
    }
};

const formatNumber = (value:number | string | undefined) => value === undefined || value === null ? "-" : Number(value).toLocaleString("en-US", {maximumFractionDigits: 2});

const StaffOrderTracker = () => {
    const { id, store_id } = useParams();
    const account = useSelector((state:RootState) => state.account);
    const { setModalLoading }: { setModalLoading:Function } = useOutletContext();

    const [searchText, setSearchText] = useState<string>("");
    const [storeName, setStoreName] = useState<string>("");
    const [vendorName, setVendorName] = useState<string>("");
    const [orderRequests, setOrderRequests] = useState<generalObj[]>([]);
    const [orderTrackers, setOrderTrackers] = useState<generalObj[]>([]);
    const [orderTrackerSummaries, setOrderTrackerSummaries] = useState<generalObj[]>([]);
    const [openTrackerModal, setOpenTrackerModal] = useState<boolean>(false);
    const [formData, setFormData] = useState<generalObj>({ order_request: null, po_number: "", order_date: "", order_value: "", po_file: null, document_url: "" });

    useEffect(() => {
        if (!account || !account.isAuthenticated || !id || !store_id) return;

        const loadForm = async () => {
            setModalLoading(true);
            const storeResult = await checkStoreByStoreIdUserId(account.token, store_id, id);
            if (!storeResult) {
                window.history.back();
                setModalLoading(false);
                return;
            }

            setStoreName(`${storeResult.data.store_data.name} - ${storeResult.data.store_data.organization}`);
            setVendorName(storeResult.data.vendor_data.username);
            const [orderRequestResult, orderTrackerResult, orderTrackerSummaryResult] = await Promise.all([
                fetchOrderRequest(account.token, store_id, id, false),
                fetchOrderTracker(account.token, store_id, id),
                fetchOrderTrackerSummary(account.token, store_id, id)
            ]);
            setOrderRequests(orderRequestResult || []);
            setOrderTrackers(orderTrackerResult || []);
            setOrderTrackerSummaries(orderTrackerSummaryResult || []);
            setModalLoading(false);
        };

        loadForm();
    }, [account, id, store_id, setModalLoading]);

    const updateFormData = (key:string, value:any) => {
        setFormData((previousData) => ({...previousData, [key]:value}));
    };

    const pickPoDocument = (event:generalObj, isDrag:boolean=false) => {
        if (!isDrag && !event.target.files.length) return;
        const file = isDrag ? event : event.target.files[0];
        if (!file || !["application/pdf", "image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
            toast("Only PDF, PNG, or JPEG files are allowed");
            return;
        }
        if (Math.floor(file.size / 1000) > 1024) {
            toast("Document size cannot exceed 1mb");
            return;
        }
        updateFormData("po_file", file);
    };

    const submitOrderTracker = async () => {
        if (!account || !account.isAuthenticated || !id || !store_id) return;
        if (!formData.order_request || !formData.po_number.trim() || !formData.order_date || formData.order_value === "") {
            toast("Order request number, PO number, order date, and order value are required");
            return;
        }
        if (Number(formData.order_value) < 0 || Number.isNaN(Number(formData.order_value))) {
            toast("Order value must be a valid amount");
            return;
        }

        setModalLoading(true);
        const result = await createOrderTracker(account.token, {
            order_request: formData.order_request.id,
            po_number: formData.po_number.trim(),
            order_date: formData.order_date,
            order_value: Number(formData.order_value),
            po_file: formData.po_file,
            vendor: id,
            store: store_id
        });
        if (result) {
            toast(result);
            const [trackerResult, trackerSummaryResult] = await Promise.all([
                fetchOrderTracker(account.token, store_id, id),
                fetchOrderTrackerSummary(account.token, store_id, id)
            ]);
            setOrderTrackers(trackerResult || []);
            setOrderTrackerSummaries(trackerSummaryResult || []);
            setFormData({ order_request: null, po_number: "", order_date: "", order_value: "", po_file: null, document_url: "" });
            setOpenTrackerModal(false);
        }
        setModalLoading(false);
    };

    const viewOrderTracker = (tracker:generalObj) => {
        if (!tracker || !tracker.month) return;

        const month = tracker.month.toString().replace("-", "_");
        window.location.href = `/staff/order_tracker/detail/${id}/${store_id}/${month}/`;
    }

    const trackedOrderRequestIds = orderTrackers.map((tracker) => tracker.order_request?.id || tracker.order_request_id || tracker.order_request).filter(Boolean);
    const availableOrderRequests = orderRequests.filter((order) => !trackedOrderRequestIds.includes(order.id)).map((order, index) => ({
        ...order,
        label: `#${order.request_number || order.order_number || order.order_request_number || order.id || index + 1}`
    }));
    
    return (
        <>
            <PageBodyHeader title="Order Tracker" desc="Track your store's order requests"
                inlineChild={<ButtonFrame btn_text="Back" btn_style={{padding:"8px 24px", borderRadius:"8px", border:"1px solid #808280",  backgroundColor:"#808280", color:"#FFF", fontSize:"14px", fontWeight:"600", letterSpacing:"-0.4px"}} onPress={() => window.history.back()} />}
            >
                <ButtonFrame btn_text="New Order Tracker" btn_style={{padding:"10px 18px", borderRadius:"8px", backgroundColor:"#1159AB", color:"#FFF", fontSize:"14px", fontWeight:"600"}} onPress={() => setOpenTrackerModal(true)} />
            </PageBodyHeader>

            <ModalParent visible={openTrackerModal}>
                <div className="ViewContainer">
                    <div className="input__pop" style={{maxWidth:"620px", width:"90vw"}}>
                        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"24px"}}>
                            <h4>New Order Tracker</h4>
                            <button type="button" title="Close" onClick={() => setOpenTrackerModal(false)} style={{fontSize:"22px", padding:"4px 10px"}}>&times;</button>
                        </div>
                        <Card color="#676967" containerCardStyle={{overflow:"auto"}}>
                            <div style={{display:"grid", gap:"20px"}}>
                                <div style={{display:"grid", gap:"20px", maxHeight:"350px", overflowX:"hidden"}}>   
                                    <InputDropdown label="Order Request Number *" placeHolder="Select order request" data={availableOrderRequests} selectedData={formData.order_request} onItemChange={(order) => updateFormData("order_request", order)} containerStyle={{width:"100%"}} />
                                    <InputField label="PO Number *" inputProp={{value:formData.po_number, placeholder:"Enter PO number"}} onTextChange={(value) => updateFormData("po_number", value)} containerStyle={{width:"100%"}} />
                                    <InputField label="Order Date *" inputProp={{value:formData.order_date, type:"date"}} onTextChange={(value) => updateFormData("order_date", value)} containerStyle={{width:"100%"}} />
                                    <InputField label="Order Value *" inputProp={{value:formData.order_value, placeholder:"Enter order value", type:"number", min:"0", step:"0.01"}} onTextChange={(value) => updateFormData("order_value", value)} containerStyle={{width:"100%"}} />
                                    <UploadField label="PO Document / Image (optional)" current_obj={formData.po_file} current_obj_url={formData.document_url} upload_description="Drag and drop a PDF or image (Max. 1mb)" file_types="application/pdf,image/png,image/jpeg,image/jpg" containerStyle={{width:"100%"}} inputContainerStyle={{height:"150px"}} onSelectFile={pickPoDocument} onInputClear={() => setFormData((previousData) => ({...previousData, po_file:null, document_url:""}))} />
                                </div>
                                <div style={{display:"flex", alignItems:"center", gap:"20px", marginTop:"12px"}}>
                                    <ButtonFrame btn_text="Save Tracker" btn_style={{padding:"12px 24px", borderRadius:"8px", backgroundColor:"#00983A", color:"#FFF", fontSize:"14px", fontWeight:"600"}} onPress={submitOrderTracker} />
                                    <ButtonFrame btn_text="Cancel" btn_style={{padding:"12px 24px", borderRadius:"8px", border:"1px solid #808280", color:"#808280", fontSize:"14px", fontWeight:"600"}} onPress={() => setOpenTrackerModal(false)} />
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </ModalParent>

            <div style={{marginTop:"30px"}}>
                <Table table_head={["Month", "Target", "Target Achieved", "Orders", "Status", "Action"]} th_class="white" color="#1159AB" thColor="#1159AB" tableSearch={<CardSearch value={searchText} onTextChange={(value) => setSearchText(value)} />} table_title={`Order requests for ${vendorName.toUpperCase()} in ${storeName}`}>
                    <>
                        {orderTrackerSummaries.filter((summary) => `${summary.month} ${summary.status}`.toLowerCase().includes(searchText.toLowerCase())).map((summary, index) => (
                            <tr key={summary.month || index}>
                                <td>{formatMonth(summary.month)}</td>
                                <td>{formatNumber(summary.target)}</td>
                                <td>{formatNumber(summary.target_achieved)}</td>
                                <td>{summary.orders ?? "-"}</td>
                                <td><span style={{padding:"4px 12px", borderRadius:"12px", fontSize:"12px", fontWeight:"600", textTransform:"capitalize", ...statusBadgeColor(summary.status || "")}}>{summary.status || "-"}</span></td>
                                
                                {/* <td style={{textTransform:"capitalize"}}>{summary.status || "-"}</td> */}
                                <td>
                                    <ButtonFrame btn_text="View" btn_style={{padding:"6px 12px", borderRadius:"4px", backgroundColor:"rgb(0, 152, 58)", color:"#FFF", fontSize:"12px", fontWeight:"600"}} onPress={() => viewOrderTracker(summary)} />
                                </td>
                            </tr>
                        ))}
                    </>
                </Table>
            </div>
        </>
    )
}

export default StaffOrderTracker;